import { useEffect, useMemo, useState } from "react";
import { Users, Search } from "lucide-react";
import PageShell from "../components/PageShell";
import { getCharacters } from "../api";
import CharacterCardDialog from "../components/CharacterCardDialog";
import CharacterAvatar from "../components/CharacterAvatar";

const GRADIENTS = [
  "from-[#C2A368] to-[#7A8450]",
  "from-[#5C8A6E] to-[#2E5A66]",
  "from-[#A06A48] to-[#5A4631]",
  "from-[#8A5A44] to-[#2D1B36]",
  "from-[#3E5A6B] to-[#10101A]",
];

const PER_PAGE = 24;

export default function Characters() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [letter, setLetter] = useState("A");
  const [visible, setVisible] = useState(PER_PAGE);
  const [active, setActive] = useState(null);

  useEffect(() => { getCharacters().then(setList).catch(() => {}); }, []);

  const baseFiltered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return list.filter((c) =>
      (filter === "ALL" || c.testament === filter) &&
      (!term || c.name.toLowerCase().includes(term) || c.tag.toLowerCase().includes(term))
    );
  }, [list, q, filter]);

  const letters = useMemo(
    () => Array.from(new Set(baseFiltered.map((c) => c.name[0].toUpperCase()))).sort(),
    [baseFiltered]
  );

  const filtered = useMemo(
    () => (q.trim() || letter === "ALL" ? baseFiltered : baseFiltered.filter((c) => c.name.toUpperCase().startsWith(letter))),
    [baseFiltered, letter, q]
  );

  useEffect(() => { setVisible(PER_PAGE); }, [q, filter, letter]);

  const shown = filtered.slice(0, visible);

  return (
    <PageShell
      title="Character Cards"
      subtitle="Meet the people of Scripture"
      icon={Users}
      accent={{ a: "#C2A368", b: "#5C8A6E", c: "#2E5A66" }}
      testid="characters-page"
    >
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center gap-3 rounded-2xl border border-border bg-background/50 px-4 py-3 backdrop-blur-md">
          <Search className="h-4 w-4 text-primary" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="characters-search"
            placeholder="Search a name — Moses, David, Esther…"
            className="font-ui w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex gap-2">
          {["ALL", "OT", "NT"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`characters-filter-${f}`}
              className={`font-ui rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                filter === f ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {f === "ALL" ? "All" : f === "OT" ? "Old Testament" : "New Testament"}
            </button>
          ))}
        </div>
      </div>

      {/* A–Z letter filter */}
      <div className="mb-6 flex flex-wrap gap-1.5" data-testid="letter-filter">
        <button
          onClick={() => setLetter("ALL")}
          data-testid="letter-ALL"
          className={`font-ui rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
            letter === "ALL" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
          }`}
        >
          All
        </button>
        {letters.map((L) => (
          <button
            key={L}
            onClick={() => setLetter(L)}
            data-testid={`letter-${L}`}
            className={`font-ui h-7 w-7 rounded-lg text-xs font-semibold transition-colors ${
              letter === L ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
            }`}
          >
            {L}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {shown.map((c, i) => (
          <button
            key={c.id}
            onClick={() => setActive(c)}
            data-testid={`character-tile-${c.id}`}
            className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-background/50 p-5 text-center backdrop-blur-md transition-all hover:border-primary/50 hover:bg-muted"
          >
            <CharacterAvatar
              cid={c.id}
              name={c.name}
              gradient={GRADIENTS[i % GRADIENTS.length]}
              className="flex h-16 w-16 items-center justify-center rounded-full shadow-md ring-1 ring-border transition-transform group-hover:scale-105"
              textClassName="text-lg"
            />
            <div>
              <p className="font-ui text-sm font-semibold leading-tight">{c.name}</p>
              <p className="font-ui mt-0.5 text-xs text-muted-foreground">{c.tag}</p>
            </div>
          </button>
        ))}
      </div>

      {filtered.length > visible && (
        <div className="mt-8 text-center">
          <button
            onClick={() => setVisible((v) => v + PER_PAGE)}
            data-testid="load-more-characters"
            className="font-ui rounded-full border border-primary/50 bg-primary/10 px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/20"
          >
            Load more ({filtered.length - visible} more)
          </button>
        </div>
      )}

      {filtered.length === 0 && list.length > 0 && (
        <p className="mt-10 text-center text-sm text-muted-foreground">No characters match “{q}”.</p>
      )}

      <CharacterCardDialog character={active} onOpenChange={setActive} />
    </PageShell>
  );
}
